import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, Paper, Stack, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { GoalsIcon } from '../global/CustomIcons';
import { fetchRequest as fetchDetail } from '../../redux/actions/discovery/goalDetail';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import StatusChip from '../common/StatusChip';
import { CMButtonOutlinedSmall, CMButtonError } from '../../containers/styledComponents';
import { SECTION_LABELS, GoalSideNav, GoalSummaryStrip } from './GoalChrome';
import GoalActivity from './GoalActivity';
import GoalSettingsCard from './GoalSettingsCard';
import GoalAlertsPanel from './GoalAlertsPanel';
import AutopilotTimelineCard from './AutopilotTimelineCard';
import ForecastStudioPage from './ForecastStudioPage';
import ScenariosPage from '../scenarios/ScenariosPage';
import FleetDashboard from './FleetDashboard';
import FleetLeaderboardPanel from './FleetLeaderboardPanel';
import FleetRunsPanel from './FleetRunsPanel';
import FleetReceiptsPanel from './FleetReceiptsPanel';
import FleetDriversPanel from './FleetDriversPanel';
import FleetBakeoffCard from './FleetBakeoffCard';
import { fmtSpan } from './goalControls';

const FLEET_TYPE_LABELS = {
  rank_by_risk: 'Rank by risk',
  rank_by_growth: 'Rank by growth',
};
const FLEET_TYPE_ICONS = {
  rank_by_risk: CrisisAlertIcon,
  rank_by_growth: TrendingUpIcon,
};

const STATUS_TONES = { idle: 'neutral', running: 'info', serving: 'success', live: 'success', failed: 'error' };

// Old fleet-only URLs keep working.
const SECTION_ALIASES = {
  runs: 'serving',
  receipts: 'activity',
  leaderboard: 'overview',
};

/**
 * Fleet goal detail — same section rail as a signal goal. Each tab is the
 * fleet equivalent (leaderboard = forecast, bake-off = models, scoring
 * runs = serving) so the two goal kinds feel like one product.
 */
const FleetGoalDetailPage = ({ goal, activity, deleteGoal, deleteState }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { goalId, section: sectionParam } = useParams();
  const raw = SECTION_ALIASES[sectionParam] || sectionParam;
  const section = SECTION_LABELS[raw] ? raw : 'overview';
  const [deleteOpen, setDeleteOpen] = useState(false);

  useRealtimeRefresh(
    eventPrefix('goal.fleet.'),
    () => dispatch(fetchDetail({ goalId })),
  );

  const fleet = goal?.fleet || {};
  const selector = goal?.target_selector || {};
  const TypeIcon = FLEET_TYPE_ICONS[goal?.goal_type] || LeaderboardIcon;
  const companionId = fleet.companion_goal_id;

  const goalMeta = useMemo(() => {
    const parts = [FLEET_TYPE_LABELS[goal?.goal_type] || goal?.goal_type];
    if (selector.value) {
      parts.push(selector.type === 'group'
        ? `group: ${selector.value}` : `pattern: ${selector.value}`);
    }
    if (fleet.member_count) parts.push(`${Number(fleet.member_count).toLocaleString()} members`);
    if (goal?.horizon_bins && goal?.interval_seconds) {
      parts.push(`${fmtSpan(goal.horizon_bins, goal.interval_seconds)} ahead`);
    }
    return parts.filter(Boolean).join('  ·  ');
  }, [goal, selector, fleet.member_count]);

  const onActivityCta = (phase) => {
    const action = phase?.cta?.action;
    const dest = {
      drivers: 'drivers',
      models: 'models',
      fit: 'models',
      deploy: 'models',
      forecast: 'forecast',
      serving: 'serving',
    }[action] || 'overview';
    navigate(`/goals/${goalId}/${dest === 'overview' ? '' : dest}`);
  };

  const renderSection = () => {
    switch (section) {
      case 'drivers':
        return <FleetDriversPanel goalId={goalId} />;

      case 'models':
        return (
          <Stack spacing={2}>
            <FleetBakeoffCard goalId={goalId} />
          </Stack>
        );

      case 'forecast':
        return (
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined">
              The chart is the <b>fleet average</b> — every member rolled into
              one series. It shows where the population as a whole is heading.
              The ranked list below is the actual product: each member scored
              {goal?.goal_type === 'rank_by_risk'
                ? ' by the chance it breaches its threshold'
                : ' by predicted growth'}
              {' '}over the next {fmtSpan(goal?.horizon_bins, goal?.interval_seconds) || 'horizon'}.
            </Alert>
            {companionId && (
              <ForecastStudioPage embedded hideTypeChip goalId={companionId} />
            )}
            <FleetLeaderboardPanel goalId={goalId} goal={goal} />
          </Stack>
        );

      case 'scenarios':
        return companionId
          ? <ScenariosPage embedded goalId={companionId} />
          : (
            <Alert severity="info">
              What-if scenarios appear once driver discovery has a fleet average to shock.
            </Alert>
          );

      case 'serving':
        return <FleetRunsPanel goalId={goalId} goal={goal} />;

      case 'alerts':
        return <GoalAlertsPanel goalId={goalId} goalType={goal?.goal_type} />;

      case 'activity':
        return (
          <Stack spacing={2}>
            {companionId && <AutopilotTimelineCard goalId={companionId} />}
            <FleetReceiptsPanel goalId={goalId} goal={goal} />
          </Stack>
        );

      case 'settings':
        return (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Population</Typography>
              <Typography variant="body2" color="text.secondary">
                {selector.type === 'group' ? 'Group' : 'Pattern'}{' '}
                <code>{selector.value}</code>
                {fleet.member_count
                  ? ` · ${Number(fleet.member_count).toLocaleString()} members`
                  : ''}
                . Membership follows this selector; scoring cadence lives with
                the automation settings below.
              </Typography>
            </Paper>
            <GoalSettingsCard goal={goal} embedded />
            <Box sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main', mb: 0.5 }}>
                Danger zone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Permanently delete this goal and everything it produced — leaderboards,
                runs, drivers and activity. This can&apos;t be undone.
              </Typography>
              <CMButtonError
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
                disabled={!goal || deleteState?.isLoading}
              >
                Delete goal
              </CMButtonError>
            </Box>
          </Stack>
        );

      case 'overview':
      default:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <FleetDashboard
                goalId={goalId}
                goal={goal}
                activity={activity}
                navigate={navigate}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
                <GoalActivity
                  orientation="vertical"
                  goalId={goalId}
                  onCta={onActivityCta}
                  onOpenForecast={() => navigate(`/goals/${goalId}/forecast`)}
                  onGoToModels={() => navigate(`/goals/${goalId}/models`)}
                />
              </Stack>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs
        items={[
          { text: 'Goals', icon: <GoalsIcon />, link: '/goals' },
          section === 'overview'
            ? { text: goal?.name || '…' }
            : { text: goal?.name || '…', link: `/goals/${goalId}` },
          ...(section === 'overview' ? [] : [{ text: SECTION_LABELS[section] }]),
        ]}
        mb={0.5}
      />

      <Stack
        direction="row" spacing={1.5} alignItems="center"
        sx={{ mb: 2.5, mt: 0.5, flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 1 }}
      >
        <Box sx={{
          width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
        }}>
          <TypeIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {goal?.name || '…'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
            {goalMeta}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0 }}>
          <Tooltip title="Ranks a population of signals on every scoring run" arrow>
            <Chip size="small" color="primary" variant="outlined" icon={<LeaderboardIcon />} label="Fleet" />
          </Tooltip>
          <StatusChip status={goal?.status} tone={STATUS_TONES[goal?.status] || 'neutral'} />
          <StatusChip
            status=""
            tone={(goal?.options?.live) !== false ? 'info' : 'neutral'}
            icon={null}
            label={(goal?.options?.live) !== false ? 'Live data' : 'Historical'}
            tooltip={(goal?.options?.live) !== false
              ? 'Data streams in continuously — scoring and driver checks stay on'
              : 'Fixed historical window — no new data arrives'}
          />
          {fleet.model && (
            <Chip size="small" variant="outlined" label={fleet.model.slug || fleet.model.family} />
          )}
        </Stack>
      </Stack>

      {fleet.last_error && (
        <Alert severity="error" sx={{ mb: 2 }}>{String(fleet.last_error)}</Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        <GoalSideNav goalId={goalId} active={section} />
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {section === 'overview' && (
            <GoalSummaryStrip phases={activity?.phases} />
          )}
          {renderSection()}
        </Paper>
      </Box>

      <Dialog
        open={deleteOpen}
        onClose={() => { if (!deleteState?.isLoading) setDeleteOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this goal?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <strong>{goal?.name || 'This goal'}</strong> and everything it produced —
            leaderboards, scoring runs, drivers and activity — will be
            permanently deleted. This can&apos;t be undone.
          </Typography>
          {deleteState?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>{String(deleteState.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CMButtonOutlinedSmall
            onClick={() => setDeleteOpen(false)}
            disabled={deleteState?.isLoading}
          >
            Cancel
          </CMButtonOutlinedSmall>
          <CMButtonError
            startIcon={deleteState?.isLoading
              ? <CircularProgress size={14} color="inherit" />
              : <DeleteOutlineIcon />}
            onClick={() => deleteGoal({ goalId })}
            disabled={deleteState?.isLoading}
          >
            {deleteState?.isLoading ? 'Deleting…' : 'Delete goal'}
          </CMButtonError>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FleetGoalDetailPage;
